import { deg, fixed } from '../../math/format';
import { sineBarAngle, sineBarStack } from '../../math/geometry';
import { mcOptions } from '../helpers';
import type { QuestionTemplate } from '../types';

const BAR_LENGTHS = [5, 10] as const;
const ANGLES = [5, 7.5, 10, 12, 15, 18, 20, 25, 30, 35, 40] as const;

export const SINEBAR_TEMPLATES: readonly QuestionTemplate[] = [
  {
    id: 'sinebar.stack',
    topicId: 'sinebar',
    skill: 'Gauge block stack for an angle',
    gen: (rng) => {
      const bar = rng.pick(BAR_LENGTHS);
      const angle = rng.pick(ANGLES);
      const solved = sineBarStack(bar, angle);
      const sine = Math.sin((angle * Math.PI) / 180);

      return {
        kind: 'mc',
        prompt: `Setting ${deg(angle, angle % 1 === 0 ? 0 : 1)} on a ${bar}" sine bar. What stack?`,
        formula: 'stack = sine bar length × sin(angle)',
        why: `sin ${deg(angle, angle % 1 === 0 ? 0 : 1)} = ${fixed(sine, 5)}, and ${bar} × that = ${fixed(solved.value, 4)}".`,
        steps: solved.steps,
        ...mcOptions(rng, `${fixed(solved.value, 4)}"`, [
          {
            label: `${fixed(bar * Math.cos((angle * Math.PI) / 180), 4)}"`,
            value: bar * Math.cos((angle * Math.PI) / 180),
            trap: 'That is cosine. A sine bar is called a sine bar for a reason — the stack is the opposite side.',
          },
          {
            label: `${fixed(bar * Math.tan((angle * Math.PI) / 180), 4)}"`,
            value: bar * Math.tan((angle * Math.PI) / 180),
            trap: 'That is tangent. Tangent works off the adjacent side, but the bar length is the hypotenuse.',
          },
          {
            label: `${fixed(sine, 4)}"`,
            value: sine,
            trap: 'That is just the sine. You still have to multiply it by the length of the bar.',
          },
        ]),
      };
    },
  },
  {
    id: 'sinebar.angle',
    topicId: 'sinebar',
    skill: 'Angle from a known stack',
    gen: (rng) => {
      const bar = rng.pick(BAR_LENGTHS);
      const angle = rng.pick(ANGLES);
      const stack = Math.round(sineBarStack(bar, angle).value * 10000) / 10000;
      const solved = sineBarAngle(bar, stack);

      return {
        kind: 'numeric',
        prompt: `A ${bar}" sine bar with ${fixed(stack, 4)}" of blocks under one roll. What angle is that?`,
        sub: 'Degrees, two places.',
        answer: solved.value,
        tolerance: 0.05,
        unit: '°',
        places: 2,
        formula: 'sin(angle) = stack ÷ bar length',
        why: `${fixed(stack, 4)} ÷ ${bar} = ${fixed(stack / bar, 5)}, and sin⁻¹ of that is ${deg(solved.value)}.`,
        steps: solved.steps,
      };
    },
  },
  {
    id: 'sinebar.whichLength',
    topicId: 'sinebar',
    skill: 'What the bar length actually measures',
    gen: (rng) => {
      const bar = rng.pick(BAR_LENGTHS);

      return {
        kind: 'mc',
        prompt: `On a ${bar}" sine bar, what does the ${bar}" refer to?`,
        formula: 'stack = sine bar length × sin(angle)',
        why: `It is the centre-to-centre distance between the two rolls. That distance is the hypotenuse of the triangle you are building, which is why sine works.`,
        steps: [
          {
            kind: 'note',
            text: 'The bar makes a right triangle: the rolls are the two ends of the hypotenuse, the stack is the opposite side.',
          },
          { kind: 'formula', text: 'stack = length × sin(angle)' },
          {
            kind: 'result',
            text: 'The length in that formula is roll centre to roll centre, not the overall bar.',
          },
        ],
        ...mcOptions(rng, 'Centre to centre of the two rolls', [
          {
            label: 'The overall length of the bar',
            trap: 'The bar usually overhangs the rolls. Using the overall length puts the angle out.',
          },
          {
            label: 'The width of the working surface',
            trap: 'The width has nothing to do with the angle. Only the roll spacing does.',
          },
          {
            label: 'The diameter of the rolls',
            trap: 'The rolls are much smaller than that, and their diameter cancels out of the setup entirely.',
          },
        ]),
      };
    },
  },
  {
    id: 'sinebar.complement',
    topicId: 'sinebar',
    skill: 'Setting an angle from the other side',
    gen: (rng) => {
      const bar = rng.pick(BAR_LENGTHS);
      const angle = rng.pick([50, 55, 60, 65, 70] as const);
      const complement = 90 - angle;
      const solved = sineBarStack(bar, complement);
      const direct = sineBarStack(bar, angle);

      return {
        kind: 'mc',
        prompt: `A print calls for ${deg(angle, 0)} off the vertical face. What stack on a ${bar}" bar?`,
        sub: 'The sine bar works from the horizontal.',
        formula: 'stack = length × sin(90° − angle)',
        why: `${deg(angle, 0)} from vertical is ${deg(complement, 0)} from horizontal, and the bar sets from horizontal. ${bar} × sin ${deg(complement, 0)} = ${fixed(solved.value, 4)}".`,
        steps: [
          {
            kind: 'note',
            text: 'A sine bar always sets an angle up from the surface plate, so an angle given off the vertical has to be flipped first.',
          },
          { kind: 'formula', text: 'stack = length × sin(90° − angle)' },
          { kind: 'substitute', text: `90 − ${angle} = ${complement}, so ${bar} × sin ${deg(complement, 0)}` },
          { kind: 'result', text: `= ${fixed(solved.value, 4)}"` },
        ],
        ...mcOptions(rng, `${fixed(solved.value, 4)}"`, [
          {
            label: `${fixed(direct.value, 4)}"`,
            value: direct.value,
            trap: `You used ${deg(angle, 0)} straight off the print. That angle is measured from the vertical, and the bar works from the horizontal.`,
          },
          {
            label: `${fixed(bar * Math.cos((complement * Math.PI) / 180), 4)}"`,
            value: bar * Math.cos((complement * Math.PI) / 180),
            trap: 'Cosine of the complement is the same as sine of the original — you flipped the angle and the function, cancelling both.',
          },
          {
            label: `${fixed(bar * Math.tan((complement * Math.PI) / 180), 4)}"`,
            value: bar * Math.tan((complement * Math.PI) / 180),
            trap: 'Tangent. The bar length is the hypotenuse, so it is always sine.',
          },
        ]),
      };
    },
  },
];
